import { useUpdateInvoice } from "@/api/invoice";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/customs/modal";
import { InvoiceDto } from "@/types/api/api-types";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

type ModalProps = {
  closeModal: () => void;
  invoice?: InvoiceDto | null;
};

export const InvoiceValidationModal = ({ closeModal, invoice }: ModalProps) => {
  const { t } = useTranslation(["boInvoices", "common"]);
  const { mutateAsync: updateInvoice, isPending } = useUpdateInvoice();
  const isPaid = invoice?.paymentStatus === "PAID";

  const handleValidationAction = async () => {
    if (!invoice) {
      return;
    }
    await updateInvoice({
      id: invoice.id,
      data: {
        paymentStatus: isPaid ? "TOBEPAID" : "PAID",
      },
    });
    closeModal();
  };

  return (
    <Modal
      open={!!invoice}
      onClose={closeModal}
      dismissible
      size="sm"
      title={
        <div className="flex items-center gap-3">
          <div
            className={`flex size-10 items-center justify-center rounded-full ${isPaid ? "bg-red-100" : "bg-green-100"}`}
          >
            {isPaid ? (
              <XCircle className="size-5 text-red-600" />
            ) : (
              <CheckCircle2 className="size-5 text-green-600" />
            )}
          </div>
          <span>{t("paymentStatusModal.title")}</span>
        </div>
      }
      description={
        isPaid
          ? t("paymentStatusModal.invalidateDescription")
          : t("paymentStatusModal.validateDescription")
      }
      footer={
        <div className="flex w-full flex-col gap-2 md:flex-row">
          <Button onClick={handleValidationAction} disabled={isPending} className="flex-1">
            {isPending ? (
              <Loader2 className="animate-spin" />
            ) : isPaid ? (
              t("paymentStatusModal.invalidate")
            ) : (
              t("paymentStatusModal.validate")
            )}
          </Button>
          <Button variant="outline" onClick={closeModal} disabled={isPending} className="flex-1">
            {t("common:actions.cancel")}
          </Button>
        </div>
      }
    />
  );
};
